import { useMemo } from 'react';
import { FEATURE_KEYS } from 'components/billing/common/constants';
import useOmniReportingAccess from 'components/omni/hooks/useOmniReportingAccess';

import { useUsageBilling } from 'components/billing/context/usageBilling';
import { useBaseMenu } from 'components/navigation/hooks/useBaseMenu';
import { useSalesMenu } from 'components/navigation/hooks/useSalesMenu';
import { useFeatureFlags } from 'controllers/contexts/featureFlags';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import useAuth from 'hooks/useAuth';
import { useEntitlements } from 'providers/entitlements/EntitlementsProvider';

import { isSalesRoute } from 'utils/isSalesRoute';

import type { NavMenuItem } from 'components/navigation/NavMenu';

export const useMenuConfig = (user: any): NavMenuItem[] => {
  const { userHasLegacyRole } = useAuth();
  const baseMenu = useBaseMenu(user);
  const salesMenu = useSalesMenu();
  const { hasPackageFeature } = useUsageBilling();
  const { hasOmniTrends } = useOmniReportingAccess();

  const { hasFlag }: any = useFeatureFlags();
  const { hasInitialized: psLabsInitialized, hasLabsFlag } = usePSLabs();
  const { isEntitledTo } = useEntitlements();

  const isSales = useMemo(() => isSalesRoute(), []);

  const menu = useMemo(
    () => (isSales ? salesMenu : baseMenu),
    [isSales, salesMenu, baseMenu],
  );

  return useMemo(() => {
    const shouldShowItem = (item: NavMenuItem): boolean => {
      if (item.id === 'admin' && !user.is_admin) {
        return false;
      }

      if (item.featureFlag || item.labsFeatureFlag) {
        let menuItemHasFlag;
        if (item.featureFlag) {
          menuItemHasFlag = hasFlag(item.featureFlag);
        }
        if (item.labsFeatureFlag && psLabsInitialized) {
          menuItemHasFlag = hasLabsFlag(item.labsFeatureFlag);
        }

        if (item.featureFlagOptOut && menuItemHasFlag) return false;
        if (!item.featureFlagOptOut && !menuItemHasFlag) return false;
      }
      return true;
    };

    const shouldShowSubItem = (
      item: Required<NavMenuItem>['subMenu'][number],
    ): boolean => {
      if (item.featureFlag || item.labsFeatureFlag) {
        let menuItemHasFlag;
        if (item.featureFlag) {
          menuItemHasFlag = hasFlag(item.featureFlag);
        }
        if (item.labsFeatureFlag && psLabsInitialized) {
          menuItemHasFlag = hasLabsFlag(item.labsFeatureFlag);
        }

        if (
          (item.featureFlagOptOut && menuItemHasFlag) ||
          (!item.featureFlagOptOut && !menuItemHasFlag)
        ) {
          return false;
        }
      }

      if (
        (item.adminOnly && !user.is_admin) ||
        (item.gatedFeatureKey &&
          !(item.gatedFeatureKey === FEATURE_KEYS.omni_trends
            ? hasOmniTrends
            : hasPackageFeature({ featureKey: item.gatedFeatureKey }))) ||
        (item.legacyRole && !userHasLegacyRole(item.legacyRole)) ||
        (item.entitlementKey && !isEntitledTo(item.entitlementKey))
      ) {
        return false;
      }

      return true;
    };

    return menu.reduce<NavMenuItem[]>((acc, item) => {
      if (!shouldShowItem(item)) {
        return acc;
      }

      const subMenu = item.subMenu?.filter(shouldShowSubItem);

      if (subMenu?.length === 0) {
        return acc;
      }

      return [...acc, { ...item, subMenu }] as NavMenuItem[];
    }, []);
  }, [
    menu,
    user,
    hasFlag,
    hasLabsFlag,
    psLabsInitialized,
    hasPackageFeature,
    hasOmniTrends,
    userHasLegacyRole,
    isEntitledTo,
  ]);
};
